/* The weekly review card: one completed Mon-Sun week, computed from the shared
 * logic modules (schedule, records events, trends, muscles, energy) so the
 * numbers match what Claude gets over MCP. Nothing is stored; the card
 * derives everything from the state. Pure, no clock: pass today in. */

import { isTrainingDate, missedDays, mondayOf, planDayFor } from '../logic/schedule';
import { dailyRollup, weeklyAdherence } from '../logic/energy';
import { volumeLandmarks, weeklyHardSets } from '../logic/muscles';
import { dayKey, dayNum, liftStatusBoard, volumeVerdict } from './statsData';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const CREATINE_RE = /creatin/i;

function isObj(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/* Monday = 0; day 0 (1970-01-01) was a Thursday. */
function monIndex(n) {
  return (((n + 3) % 7) + 7) % 7;
}

/**
 * The Monday of the last completed Mon-Sun week, or `back` weeks before it.
 * @param {string} today YYYY-MM-DD workout day
 * @param {number} [back] 0 = last week
 * @returns {string|null}
 */
export function reviewWeekStart(today, back = 0) {
  const m = mondayOf(today);
  return m ? dayKey(dayNum(m) - 7 * (1 + Math.max(0, back))) : null;
}

/** ISO 8601 week number of a date. */
export function isoWeek(date) {
  const n = dayNum(date);
  if (n === null) return null;
  const thursday = n - monIndex(n) + 3;
  const year = dayKey(thursday).slice(0, 4);
  const jan4 = dayNum(`${year}-01-04`);
  return Math.floor((thursday - (jan4 - monIndex(jan4))) / 7) + 1;
}

/** 'Monday'..'Sunday' for a date key. */
export function weekdayName(date) {
  const n = dayNum(date);
  return n === null ? '' : WEEKDAYS[monIndex(n)];
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/* The rule-based one-liner on top of the card. A missed session is the most
 * useful thing to see (Mo's misses cluster on leg days), then a PR haul,
 * then low protein, then a clean week. */
function headlineOf(sessions, prs, nutrition) {
  const prText = prs.length ? ` · ${plural(prs.length, 'PR')}` : '';
  if (sessions.missed.length === 1) {
    const m = sessions.missed[0];
    return `Missed ${m.planDay || 'a session'} on ${weekdayName(m.date)}${prText}`;
  }
  if (sessions.missed.length > 1) {
    const names = [...new Set(sessions.missed.map(m => m.planDay).filter(Boolean))];
    return `Missed ${sessions.missed.length} sessions${names.length ? ` (${names.join(', ')})` : ''}${prText}`;
  }
  if (prs.length >= 3) {
    const names = [...new Set(prs.map(e => e.exercise))];
    const shown = names.slice(0, 2).join(', ') + (names.length > 2 ? ', …' : '');
    return `Strong week: ${prs.length} PRs (${shown})`;
  }
  if (nutrition && nutrition.proteinHitDays !== null && nutrition.loggedDays >= 3 && nutrition.proteinHitDays * 2 < nutrition.loggedDays) {
    return `Protein hit on ${nutrition.proteinHitDays} of ${nutrition.loggedDays} logged days`;
  }
  if (sessions.planned > 0 && sessions.done >= sessions.planned) {
    return `All ${plural(sessions.planned, 'planned session')} done${prText}`;
  }
  if (sessions.done > 0) return `${plural(sessions.done, 'session')}${prText}`;
  return 'No sessions logged';
}

/**
 * Everything the weekly review card shows for one Mon-Sun week.
 * @param {object} state the fitness state
 * @param {string} weekStart the Monday, YYYY-MM-DD
 * @param {{today:string, events?:Array}} opts events = buildRecords().events
 * @returns {null|{weekStart, weekEnd, week:number, headline:string, hasData:boolean,
 *   sessions:{done, planned, extra, missed:Array<{date, planDay}>},
 *   prs:Array, movers:{up:Array, down:Array},
 *   muscles:{top:Array<{muscle, sets}>, total:number, verdict:object|null},
 *   nutrition:object|null, weight:{avg:number|null, change:number|null},
 *   habits:Array<{id, label, cells:Array<'hit'|'miss'|'na'>, hit:number, of:number}>}}
 *   habit cells run Monday..Sunday; 'na' = not applicable (a rest day for
 *   Train, an unlogged day for Protein)
 */
export function buildWeekReview(state, weekStart, { today, events = [] } = {}) {
  const ws = dayNum(weekStart);
  if (ws === null || !isObj(state)) return null;
  const weekEnd = dayKey(ws + 6);
  const dates = Array.from({ length: 7 }, (_, i) => dayKey(ws + i));
  const fallback = mondayOf(today) || weekStart;

  // Sessions: planned = non-rest plan days; missed only counts from the
  // first logged session on (missedDays), so a new user isn't told off.
  const missed = missedDays(state, weekStart, weekEnd, today);
  const missedSet = new Set(missed.map(m => m.date));
  let planned = 0;
  let done = 0;
  let extra = 0;
  const trainCells = dates.map(date => {
    const p = planDayFor(state, date, fallback);
    const isPlanned = !!p && isObj(p.day) && !p.day.isRest;
    const trained = isTrainingDate(state, date);
    if (isPlanned) planned += 1;
    if (trained) done += 1;
    if (trained && !isPlanned) extra += 1;
    if (trained) return 'hit';
    return missedSet.has(date) ? 'miss' : 'na';
  });
  const sessions = { done, planned, extra, missed };

  const prs = (Array.isArray(events) ? events : []).filter(e => e && e.date >= weekStart && e.date <= weekEnd);

  // Movers: the lift status as of Sunday, for lifts trained this week. The
  // board looks back 8 weeks so its stall rules match the Stats card.
  const trainedNames = new Set();
  dates.forEach(date => {
    const day = isObj(state.logs) ? state.logs[date] : null;
    if (!isObj(day)) return;
    Object.keys(day).forEach(name => {
      if (Array.isArray(day[name]) && day[name].some(s => isObj(s) && s.done && Number(s.weight) > 0 && Number(s.reps) > 0)) {
        trainedNames.add(name);
      }
    });
  });
  const board = trainedNames.size ? liftStatusBoard(state, weekEnd).filter(r => trainedNames.has(r.name)) : [];
  const up = board.filter(r => r.trend.status === 'progressing')
    .sort((a, b) => (b.trend.slopePctPerWeek || 0) - (a.trend.slopePctPerWeek || 0));
  const down = board.filter(r => r.trend.status === 'regressing' || r.trend.status === 'stalled');

  const hard = weeklyHardSets(state, weekStart);
  const top = Object.entries(hard.byMuscle).map(([muscle, sets]) => ({ muscle, sets }))
    .sort((a, b) => b.sets - a.sets || a.muscle.localeCompare(b.muscle));
  const verdict = hard.total > 0 ? volumeVerdict(hard.byMuscle, volumeLandmarks(state), state) : null;

  const adh = weeklyAdherence(state, weekStart, weekEnd)[0] || null;
  const nutrition = adh && adh.rawAvg ? {
    loggedDays: adh.loggedDays,
    avgKcal: adh.avg ? adh.avg.kcal : null,
    avgProtein: adh.avg ? adh.avg.protein : null,
    kcalOnTargetDays: adh.kcalOnTargetDays,
    proteinHitDays: adh.proteinHitDays,
    proteinPerKg: adh.proteinPerKg,
  } : null;

  // Habit strip. Protein uses weeklyAdherence's rule (a complete day at
  // >= 90% of the target) so the dots add up to its count.
  const rollup = dailyRollup(state, weekStart, weekEnd);
  const proteinTarget = Number(state.profile?.macros?.protein) || 0;
  const habits = [{ id: 'train', label: 'Train', cells: trainCells, hit: done, of: planned }];
  if (proteinTarget > 0) {
    const cells = rollup.map(r => {
      if (r.status !== 'complete' || !r.items) return 'na';
      return r.protein * 10 >= proteinTarget * 9 ? 'hit' : 'miss';
    });
    habits.push({ id: 'protein', label: 'Protein', cells, hit: cells.filter(x => x === 'hit').length, of: cells.filter(x => x !== 'na').length });
  }
  const items = Array.isArray(state.supplements?.items) ? state.supplements.items : [];
  const tracksCreatine = items.some(i => isObj(i) && CREATINE_RE.test(String(i.name || '')))
    || rollup.some(r => r.supplements.some(s => CREATINE_RE.test(s)));
  if (tracksCreatine) {
    const cells = rollup.map(r => (r.supplements.some(s => CREATINE_RE.test(s)) ? 'hit' : 'miss'));
    habits.push({ id: 'creatine', label: 'Creatine', cells, hit: cells.filter(x => x === 'hit').length, of: 7 });
  }
  const weighCells = rollup.map(r => (r.weight !== null ? 'hit' : 'miss'));
  habits.push({ id: 'weigh', label: 'Weigh-in', cells: weighCells, hit: weighCells.filter(x => x === 'hit').length, of: 7 });

  const hasData = done > 0 || missed.length > 0 || !!nutrition || weighCells.includes('hit');
  return {
    weekStart,
    weekEnd,
    week: isoWeek(weekStart),
    headline: headlineOf(sessions, prs, nutrition),
    hasData,
    sessions,
    prs,
    movers: { up, down },
    muscles: { top, total: hard.total, verdict },
    nutrition,
    weight: { avg: adh ? adh.avgWeight : null, change: adh ? adh.weightChange : null },
    habits,
  };
}
